/**
 * <cart-recommendations> — carousel of upsell / cross-sell products in the cart
 * drawer and on the cart page (snippets/cart-recommendations.liquid).
 *
 * The list itself is rendered server-side and re-rendered by cart.js on every
 * cart change, so this element only:
 *  - initialises the Swiper carousel (with its prev / next buttons),
 *  - handles the one-click add for single-variant products
 *    (multi-variant products go through the quick-cart-drawer trigger).
 */
if (!customElements.get('cart-recommendations')) {
  class CartRecommendations extends HTMLElement {
    constructor() {
      super();
      this.slider = null;
      this.onAddClick = this.onAddClick.bind(this);
    }

    connectedCallback() {
      this.initSlider();
      this.addEventListener('click', this.onAddClick);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onAddClick);
      if (this.slider) {
        this.slider.destroy(true, true);
        this.slider = null;
      }
    }

    initSlider() {
      const container = this.querySelector('.swiper');
      if (!container) return;

      // swiper-bundle.min.js is loaded with `defer`: it may not be ready yet
      // when the drawer is rendered on first paint.
      if (typeof Swiper === 'undefined') {
        if (document.readyState === 'complete') {
          setTimeout(() => this.isConnected && this.initSlider(), 100);
        } else {
          window.addEventListener('load', () => this.initSlider(), { once: true });
        }
        return;
      }
      if (this.slider) return;

      this.slider = new Swiper(container, {
        slidesPerView: 'auto',
        spaceBetween: 12,
        watchOverflow: true,
        navigation: {
          prevEl: this.querySelector('[data-rec-prev]'),
          nextEl: this.querySelector('[data-rec-next]'),
          disabledClass: 'swiper-button-disabled'
        },
        a11y: {
          enabled: true
        }
      });
    }

    getCart() {
      return (
        document.querySelector('cart-drawer') ||
        document.querySelector('cart-items')
      );
    }

    onAddClick(event) {
      const button = event.target.closest('.cart-recommendations__add');
      if (!button || !this.contains(button)) return;
      event.preventDefault();
      this.addVariant(button);
    }

    addVariant(button) {
      const variantId = Number(button.dataset.variantId);
      if (!variantId || button.classList.contains('is--loading')) return;

      const cart = this.getCart();
      const sections =
        cart && typeof cart.getSectionsToRender === 'function'
          ? cart.getSectionsToRender()
          : [];

      button.classList.add('is--loading');
      button.setAttribute('disabled', '');
      this.classList.add('is-loading');

      const config = fetchConfig();
      config.body = JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
        sections: sections.map(section => section.section),
        sections_url: window.location.pathname
      });

      fetch(`${routes.cart_add_url}`, config)
        .then(response => response.json())
        .then(response => {
          if (response.status) {
            // Shopify error payload ({ status, message, description })
            this.showError(button, response.description || response.message);
            return;
          }

          // Replace each cart section with its fresh server render. This is the
          // same mechanism as cart.js (CartItems.updateQuantity) and works both
          // for the drawer (#CartDrawer-Body) and the cart page (cart-items).
          sections.forEach(section => {
            const html = response.sections && response.sections[section.section];
            if (!html) return;
            const target =
              document.querySelector(section.selector) ||
              document.querySelector(section.id);
            const fresh = new DOMParser()
              .parseFromString(html, 'text/html')
              .querySelector(section.selector);
            if (target && fresh) {
              target.innerHTML = fresh.innerHTML;
            }
          });

          // From the cart page the drawer stays closed (the page cart has just
          // been refreshed); elsewhere the drawer is already open around us.
          const drawer = document.querySelector('cart-drawer');
          if (
            drawer &&
            !drawer.classList.contains('is-visible') &&
            window.location.pathname !== routes.cart_url
          ) {
            drawer.open();
          }

          if (typeof updateCartCounters === 'function') updateCartCounters();
          if (typeof updateFreeShipping === 'function') updateFreeShipping();

          this.dispatchEvent(
            new CustomEvent('cart-recommendations:added', {
              bubbles: true,
              detail: { variantId, response }
            })
          );
        })
        .catch(error => {
          console.error(error);
        })
        .finally(() => {
          // The element is usually replaced by the re-render above; these are
          // for the error path.
          button.classList.remove('is--loading');
          button.removeAttribute('disabled');
          this.classList.remove('is-loading');
        });
    }

    showError(button, message) {
      if (!message) return;
      let error = button.parentElement.querySelector('.cart-recommendations__error');
      if (!error) {
        error = document.createElement('p');
        error.className = 'cart-recommendations__error';
        error.setAttribute('role', 'alert');
        button.parentElement.appendChild(error);
      }
      error.textContent = message;
      setTimeout(() => error.remove(), 5000);
    }
  }

  customElements.define('cart-recommendations', CartRecommendations);
}
